/* eslint-disable @typescript-eslint/no-unused-vars */
/* for mobx decorators? */
import '@progress/kendo-theme-material/dist/all.css';

import { Button } from '@progress/kendo-react-buttons';
import { Calendar, CalendarCellProps } from '@progress/kendo-react-dateinputs';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { FlexBox } from '@twilio/flex-ui';
import { inject, observer } from 'mobx-react';
import { Moment } from 'moment';
import moment from 'moment-timezone';
import React, { Component } from 'react';

import { DateTimeFormat } from '../../../../interfaces/ICalendar';
import { ITimeSlot } from '../../../../interfaces/ITimeSlot';
import ChatStore from '../../../../stores/ChatStore';
import { ChatCalendarCell, Container, TimeSlotConfirmButton } from './styled';

export interface ITimeSlotPickerProps {
  data: any;
  messageId: string;
  timezone: string;
  scrollMessageList: () => void;
}

export interface ITimeSlotPickerState {
  timeSlots: ITimeSlot[];
  datesWithTimeSlots: string[];
  selectedDate?: Date;
  dropdownSelectedTimeSlot?: string;
}

const { DATE_COMPARISON: DATE_COMPARISON_FORMAT, DATE_HEADER: DATE_HEADER_FORMAT } = DateTimeFormat;

export interface IInjectedProps extends ITimeSlotPickerProps {
  chatStore: ChatStore;
}

const CALENDAR_WIDTH = 414;

@inject('chatStore')
@observer
class TimeSlotPicker extends Component<ITimeSlotPickerProps, ITimeSlotPickerState> {
  protected get injected(): IInjectedProps {
    return this.props as IInjectedProps;
  }

  constructor(props: ITimeSlotPickerProps) {
    super(props);

    let timeSlots: ITimeSlot[] = [];

    if (this.props.data) {
      timeSlots = [...this.props.data] as ITimeSlot[];
    }

    const datesWithTimeSlots = this.getDayNumbersWithSlots(timeSlots);

    this.state = {
      datesWithTimeSlots,
      timeSlots,
    };
  }

  public render() {
    const { timezone } = this.props;
    const { timeSlots, selectedDate } = this.state;
    const { min, max } = this.getMinMaxDatesFromData(timeSlots);

    moment.tz.setDefault(timezone);

    const timeSlotsForSelectedDay = this.getTimeSlotsForSelectedDay();
    const formatedSelectedDate = selectedDate ? moment(selectedDate).format(DATE_HEADER_FORMAT) : '';

    // scale calendar down for small formats
    const frameWidth = window.innerWidth;
    const frameSmallerThanCalendar = frameWidth < CALENDAR_WIDTH;
    const transform = frameSmallerThanCalendar ? this.getTransform() : undefined;

    return (
      <Container transform={transform}>
        <Calendar
          // navigation={false}
          min={min.toDate()}
          max={max.toDate()}
          cell={this.renderCell}
          value={selectedDate}
          onChange={async (event) => {
            await this.setState({ selectedDate: event.value });
            this.props.scrollMessageList();
          }}
        />
        {selectedDate && (
          <div>
            <p>{formatedSelectedDate}</p>
            {this.displayTimeSlotView(timeSlotsForSelectedDay, formatedSelectedDate)}
          </div>
        )}
      </Container>
    );
  }

  private renderCell = (props: CalendarCellProps) => {
    const { value } = props;
    const { datesWithTimeSlots } = this.state;
    const dateMoment = moment(value);

    const disabled = datesWithTimeSlots.includes(dateMoment.format(DATE_COMPARISON_FORMAT)) ? false : true;

    return <ChatCalendarCell {...props} isDisabled={disabled} />;
  };

  private getMinMaxDatesFromData(data: ITimeSlot[]) {
    let min: Moment = moment();
    let max: Moment = moment();

    if (data.length > 0) {
      min = moment(data[0].startTime);
      max = moment(data[data.length - 1].endTime);
    }

    return { min, max };
  }

  private getDayNumbersWithSlots = (timeSlots: ITimeSlot[]) => {
    return timeSlots.reduce((result: string[], timeSlot) => {
      const start = moment(timeSlot.startTime);

      const day = start.format(DATE_COMPARISON_FORMAT);

      if (!result.includes(day)) {
        result.push(day);
      }

      return result;
    }, []);
  };

  private getTimeSlotsForSelectedDay = (): ITimeSlot[] => {
    const { selectedDate, timeSlots } = this.state;

    if (!selectedDate || timeSlots.length === 0) {
      return [];
    }

    return timeSlots.filter(({ startTime }) => moment(startTime).isSame(moment(selectedDate), 'day'));
  };

  private getTransform = (): string => {
    const frameWidth = window.innerWidth;
    const scale = frameWidth / CALENDAR_WIDTH;
    const translate = ((((1 - scale) * 1.1) / 2) * -100).toFixed(2);
    const transform = `scale(${scale.toFixed(2)}) translate(${translate}%, ${translate}%)`;
    return transform;
  };

  private displayTimeSlotView = (timeSlotsForSelectedDay: ITimeSlot[], formatedSelectedDate: string) => {
    const { chatStore } = this.injected;
    const { messageId, timezone } = this.props;
    const { dropdownSelectedTimeSlot } = this.state;
    const TIMESLOT_DISPLAY_LIMIT = 6;

    // compile timeslots
    const timeSlots = timeSlotsForSelectedDay.map((timeSlot) => {
      const { endTime, startTime } = timeSlot;
      let { label } = timeSlot;
      if (!label) {
        const endLabel = moment.tz(endTime, timezone).format(DateTimeFormat.TIME_END);
        const startLabel = moment.tz(startTime, timezone).format(DateTimeFormat.TIME_START);

        label = `${startLabel}-${endLabel}`;
      }
      const message = `${formatedSelectedDate} ${label}`;

      return {
        label,
        message,
      };
    });

    if (timeSlotsForSelectedDay.length > TIMESLOT_DISPLAY_LIMIT) {
      const dropdownData = timeSlots.map((timeSlot) => timeSlot.label);

      const selectedTimeSlot = dropdownSelectedTimeSlot
        ? timeSlots.find((timeSlot) => timeSlot.label === dropdownSelectedTimeSlot)
        : '';

      return (
        <FlexBox>
          <DropDownList
            defaultValue={'Select a timeslot'}
            data={dropdownData}
            onChange={(event: DropDownListChangeEvent) => this.setState({ dropdownSelectedTimeSlot: event.value })}
          />
          {selectedTimeSlot && (
            <TimeSlotConfirmButton
              primary
              look='outline'
              autoFocus
              onClick={() => {
                chatStore.sendSimpleMessage(selectedTimeSlot.message, messageId, true);
              }}
              key={'dropdownConfirmButton'}
            >
              Confirm
            </TimeSlotConfirmButton>
          )}
        </FlexBox>
      );
    } else {
      return timeSlots.map((timeSlot, index) => {
        return (
          <Button
            primary
            look='outline'
            autoFocus
            onClick={() => {
              chatStore.sendSimpleMessage(timeSlot.message, messageId, true);
            }}
            key={index}
          >
            {timeSlot.label}
          </Button>
        );
      });
    }
  };
}

export default TimeSlotPicker;
